// The list pane's search box (docs/06-ux-rules.md "List"; prototype/v69
// `.ib-search-top`, index.html:1849). Debounce/cancel semantics live in
// `useDebouncedSearch`; this component is purely presentational.
import { Button } from '@thanx/uikit/button'
import { Input } from '@thanx/uikit/input'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { useDebouncedSearch } from './useDebouncedSearch'

const t = buildTranslate('feedback_inbox.list')

// Sketch reference (v82, order-feedback-landing-page/v2): pixel-sampled
// `#F8F9FA` fill / `#e9eaec` border — exact matches for palette.grey05/
// grey20 — and a visibly more-rounded corner than baseui's own default,
// matching segmentRowStyle's 8px right above this in the filter bar
// (InboxFilterBar/index.tsx).
//
// The radius has to live on `Root`, not `Input`: baseui's Input renders the
// visible border on the outer `Root` slot, while `Input` styles only the
// bare native `<input>` inside it — a radius on `Input` alone leaves
// `Root`'s own (default) corners showing through, mismatched against the
// fill.
//
// `StartEnhancer` (the search icon): uikit's own slot style pads it 18px
// either side on a grey10 fill with a right-hand divider, and uikit's input
// adds 16px more — so the icon sat in its own darker block, far from both
// the edge and the text. v83's box (`.ib-search-top .box`) is one flat
// fill: 12px padding, then a 9px gap to the text. Exported so the values
// are unit-testable (css() is a no-op in the default jsdom test env).
const SEARCH_ICON_INSET_PX = 12
const SEARCH_ICON_GAP_PX = 9

export const searchInputOverrides = {
  Input: {
    style: () => ({
      backgroundColor: palette.grey05,
      paddingLeft: `${SEARCH_ICON_GAP_PX}px`,
    }),
  },
  Root: {
    style: () => ({
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
      // uikit's own Root style zeroes these; replacing it re-exposes
      // baseui's default Root padding as a darker strip at the edge.
      paddingLeft: '0px',
      paddingRight: '0px',
      backgroundColor: palette.grey05,
    }),
  },
  StartEnhancer: {
    style: () => ({
      paddingLeft: `${SEARCH_ICON_INSET_PX}px`,
      paddingRight: '0px',
      backgroundColor: palette.grey05,
      borderRightWidth: '0px',
      color: palette.grey70,
    }),
  },
}

export interface SearchHeaderProps {
  q: string
  onSearchChange: (q: string) => void
}

const SearchHeader = ({ q, onSearchChange }: SearchHeaderProps) => {
  const [css] = useStyletron()
  const { draft, onDraftChange, clear } = useDebouncedSearch(q, onSearchChange)

  return (
    <div
      className={css({
        position: 'relative',
        padding: '12px 16px',
        borderBottom: `1px solid ${palette.grey20}`,
      })}
    >
      <Input
        value={draft}
        onChange={onDraftChange}
        placeholder={t('search_placeholder')}
        aria-label={t('search_aria')}
        startEnhancer={() => (
          <i
            className="fa fa-search"
            aria-hidden="true"
            style={{ color: palette.grey70 }}
          />
        )}
        overrides={searchInputOverrides}
      />
      {draft.length > 0 && (
        <Button
          kind="minimal"
          onClick={clear}
          aria-label={t('clear_search')}
          overrides={{
            // `overrides.BaseButton.style` REPLACES @thanx/uikit's own
            // `kind`-based style function (see InboxFilterBar/index.tsx's
            // header comment for the full story) — so background/border,
            // INCLUDING the `:hover` background below, have to be
            // re-declared here, or the state falls through to baseui's
            // default `$kind = primary` rather than reading as borderless.
            BaseButton: {
              style: () => ({
                position: 'absolute',
                right: '22px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '4px',
                minWidth: 0,
                height: 'auto',
                backgroundColor: 'transparent',
                border: 'none',
                color: palette.grey70,
                ':hover': {
                  backgroundColor: palette.grey10,
                },
              }),
            },
          }}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}

export default SearchHeader
